<!--
  NOTE<Jason>: Components passed by the `steps` prop must (!) have a single root
  node for proper transitions, or you'll end up scratching your head over why
  your content is not rendering.
-->

<template>
  <div
    class="wl-content-stepper"
    ref="stepperRef"
    :class="[transition, `direction-${transitionDirection}`]"
  >
    <Transition
      name="wl-content-stepper-transition"
      mode="out-in"
      @before-enter="emit('before-enter')"
      @after-enter="handleAfterEnter"
      @enter="handleEnter"
      @before-leave="handleBeforeLeave"
      @after-leave="emit('after-leave')"
    >
      <slot
        name="step"
        :current-step="currentStep"
        :current-step-definition="currentStepDefinition"
        :go-to="handleGoTo"
        :prev="handlePrev"
        :next="handleNext"
        :is-first-step="isFirstStep"
        :is-last-step="isLastStep"
        :previous-step="previousStep"
        :step-history="stepHistory"
        :history-index-of="historyIndexOf"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts" generic="T extends string | number">
import { computed, ref, shallowRef, useTemplateRef, triggerRef } from "vue";
import type {
  StepperProps,
  GoToOptions,
  TransitionDirection,
  StepDefinition,
  ContentStepperEmits,
  ContentStepperStepProps,
  ContentStepperDefineExpose,
} from "./types";

const emit = defineEmits<ContentStepperEmits>();
const { steps, transition = "fade-slide" } = defineProps<StepperProps<T>>();
// Allow for the return type that the documentation recommends:
// https://vuejs.org/api/sfc-script-setup.html#defineslots
// eslint-disable-next-line @typescript-eslint/no-explicit-any
defineSlots<{ step(props: ContentStepperStepProps<T>): any }>();

const currentStep = defineModel<T>({ required: true });
const stepperRef = useTemplateRef("stepperRef");
const stepIndexMap = computed(
  () => new Map(steps.map((step, index) => [step.id, index])),
);
const stepRegistryMap = computed(
  () => new Map(steps.map((step) => [step.id, step])),
);
// NOTE: We use `shallowRef` over `ref` here because Vue's type inference
// resolves `ref<T[]>` to `Ref<UnwrapRefSimple<T>[]>` for generic types, causing
// a type error even when T is constrained to `string | number`. Mutations
// require `triggerRef`.
const stepHistory = shallowRef<T[]>([currentStep.value]);
const transitionDirection = ref<TransitionDirection>("next");

const currentStepDefinition = computed((): StepDefinition<T> | undefined => {
  const step = stepRegistryMap.value.get(currentStep.value);
  if (import.meta.env.DEV && !step) {
    /* eslint-disable-next-line no-console */
    console.error(
      `[ContentStepper] "${currentStep.value}" is not found in stepRegistryMap`,
    );
  }

  return step;
});

const isFirstStep = computed(() => {
  return stepIndexMap.value.get(currentStep.value) === 0;
});

const isLastStep = computed(() => {
  return stepIndexMap.value.get(currentStep.value) === steps.length - 1;
});

const previousStep = computed<T | undefined>(
  () => stepHistory.value[stepHistory.value.length - 2],
);

const historyIndexOf = (step: T): number => stepHistory.value.lastIndexOf(step);

let leavingHeight = 0;

const handleBeforeLeave = (element: Element): void => {
  // Read
  leavingHeight = (element as HTMLElement).offsetHeight;

  // Write, deferred to next frame so it doesn't interleave with other pending writes
  requestAnimationFrame(() => {
    if (stepperRef.value) {
      stepperRef.value.style.height = `${leavingHeight}px`;
    }
  });

  emit("before-leave");
};

const handleEnter = (element: Element): void => {
  const enteringHeight = (element as HTMLElement).offsetHeight;

  requestAnimationFrame(() => {
    if (stepperRef.value) {
      stepperRef.value.style.height = `${enteringHeight}px`;
    }
  });
};

const handleAfterEnter = (): void => {
  if (stepperRef.value) {
    stepperRef.value.style.height = "auto";
  }

  emit("after-enter");
};

const resetStepHistory = (): void => {
  stepHistory.value = [currentStep.value];
  triggerRef(stepHistory);
};

const getTransitionDirection = (targetStep: T): TransitionDirection => {
  const currentIndex = stepIndexMap.value.get(currentStep.value);
  const targetIndex = stepIndexMap.value.get(targetStep);
  if (currentIndex === undefined || targetIndex === undefined) {
    return "next";
  }

  return targetIndex > currentIndex ? "next" : "prev";
};

const getStepIdAtOffset = (offset: number): T | undefined => {
  const index = stepIndexMap.value.get(currentStep.value);
  if (index === undefined) return;

  const target = steps[index + offset];
  return target?.id;
};

const navigate = (offset: number, direction: TransitionDirection): void => {
  const step = getStepIdAtOffset(offset);
  if (!step) return;

  handleGoTo(step, { transitionDirection: direction });
};

const handleGoTo = (step: T, options?: GoToOptions): void => {
  if (!stepIndexMap.value.has(step)) {
    if (import.meta.env.DEV) {
      /* eslint-disable-next-line no-console */
      console.error(`[ContentStepper] "${step}" is an invalid step id`);
    }
    return;
  }

  transitionDirection.value =
    options?.transitionDirection ?? getTransitionDirection(step);
  stepHistory.value.push(step);
  triggerRef(stepHistory);
  currentStep.value = step;
};

const handlePrev = (): void => navigate(-1, "prev");

const handleNext = (): void => navigate(1, "next");

defineExpose<ContentStepperDefineExpose<T>>({
  next: handleNext,
  prev: handlePrev,
  goTo: handleGoTo,
  historyIndexOf,
  stepHistory,
  resetStepHistory,
  isFirstStep,
  isLastStep,
  previousStep,
});
</script>

<style>
@reference "@theme";

.wl-content-stepper {
  @apply relative h-fit w-full;
  transition: height 125ms ease 125ms;
}

.wl-content-stepper.fade {
  .wl-content-stepper-transition-enter-active {
    transition:
      opacity 125ms cubic-bezier(0.4, 0, 0.2, 1) 125ms,
      translate 125ms cubic-bezier(0.4, 0, 0.2, 1) 125ms;
  }
  .wl-content-stepper-transition-leave-active {
    transition:
      opacity 125ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      translate 125ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  }
  .wl-content-stepper-transition-enter-from,
  .wl-content-stepper-transition-leave-to {
    @apply opacity-0;
  }
}

.wl-content-stepper.fade-slide {
  .wl-content-stepper-transition-enter-active {
    transition:
      opacity 125ms cubic-bezier(0.4, 0, 0.2, 1) 125ms,
      translate 125ms cubic-bezier(0.4, 0, 0.2, 1) 125ms;
  }
  .wl-content-stepper-transition-leave-active {
    transition:
      opacity 125ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      translate 125ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  }

  &.direction-prev {
    .wl-content-stepper-transition-enter-from {
      @apply -translate-x-8 opacity-0;
    }
    .wl-content-stepper-transition-leave-to {
      @apply translate-x-8 opacity-0;
    }
  }

  &.direction-next {
    .wl-content-stepper-transition-enter-from {
      @apply translate-x-8 opacity-0;
    }
    .wl-content-stepper-transition-leave-to {
      @apply -translate-x-8 opacity-0;
    }
  }
}

.wl-content-stepper-transition-leave-active {
  @apply absolute top-0;
}
</style>
